import { useState, useEffect } from "react";
import { Plus, Trash, X, HardHat, PencilSimple, Check } from "@phosphor-icons/react";
import { mergeContractorsWithDefaults } from "../../features/projects/contractors/model";

// ─── Read-only view ───────────────────────────────────────────────────────────
function ReadView({ contractors, onEdit }) {
  const active = contractors.filter((c) => !c.not_needed);
  const notNeeded = contractors.filter((c) => c.not_needed);

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="contractors-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
          <HardHat size={12} className="text-slate-400" />
          Contractors
        </h3>
        <button
          onClick={onEdit}
          data-testid="contractors-edit-btn"
          className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-slate-800 border border-slate-200 hover:border-slate-300 px-2 py-1 rounded transition-colors"
        >
          <PencilSimple size={10} />
          Edit
        </button>
      </div>

      {active.length === 0 && notNeeded.length === 0 ? (
        <p className="text-xs text-slate-400 italic">No contractors configured yet.</p>
      ) : (
        <div className="space-y-2">
          {active.map((c) => (
            <div key={c.type} className="text-xs" data-testid={`contractor-row-${c.type}`}>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-slate-700 w-28 flex-shrink-0">{c.type}</span>
                {c.contacts.length === 0 ? (
                  <span className="text-slate-400 italic">—</span>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {c.contacts.map((ct, i) => (
                      <span key={i} className="text-slate-600">
                        {ct.name && <span className="font-medium">{ct.name}</span>}
                        {ct.name && ct.email && <span className="text-slate-400"> · </span>}
                        {ct.email && (
                          <a href={`mailto:${ct.email}`} className="text-[#0078D4] hover:underline">
                            {ct.email}
                          </a>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {notNeeded.length > 0 && (
            <div className="pt-1 mt-1 border-t border-slate-100 flex flex-wrap gap-1.5">
              {notNeeded.map((c) => (
                <span key={c.type} className="text-[10px] text-slate-400 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-sm">
                  {c.type} — not needed
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Single contractor row in edit mode ──────────────────────────────────────
function ContractorEditRow({ contractor, onChange, onRemove }) {
  const addContact = () =>
    onChange({ ...contractor, contacts: [...contractor.contacts, { name: "", email: "" }] });

  const updateContact = (i, field, val) =>
    onChange({
      ...contractor,
      contacts: contractor.contacts.map((c, idx) =>
        idx === i ? { ...c, [field]: val } : c
      ),
    });

  const removeContact = (i) =>
    onChange({ ...contractor, contacts: contractor.contacts.filter((_, idx) => idx !== i) });

  const toggleNotNeeded = () =>
    onChange({ ...contractor, not_needed: !contractor.not_needed });

  return (
    <div
      className="p-3 rounded-sm border border-slate-200 bg-white space-y-2"
      data-testid={`contractor-edit-row-${contractor.type}`}
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800">{contractor.type}</span>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={contractor.not_needed}
              onChange={toggleNotNeeded}
              className="accent-slate-500 w-3.5 h-3.5"
              data-testid={`contractor-not-needed-${contractor.type}`}
            />
            <span className="text-[11px] text-slate-500">Not needed</span>
          </label>
          {!contractor.is_default && (
            <button
              type="button"
              onClick={onRemove}
              title="Remove this contractor"
              className="text-slate-400 hover:text-red-500 transition-colors"
              data-testid={`contractor-remove-${contractor.type}`}
            >
              <Trash size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Contacts */}
      {!contractor.not_needed && (
        <div className="pl-1 space-y-1.5">
          {contractor.contacts.map((ct, i) => (
            <div key={i} className="flex items-center gap-1.5" data-testid={`contractor-contact-row-${contractor.type}-${i}`}>
              <input
                type="text"
                placeholder="Name"
                value={ct.name}
                onChange={(e) => updateContact(i, "name", e.target.value)}
                className="w-32 h-7 px-2 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary/40"
                data-testid={`contractor-name-${contractor.type}-${i}`}
              />
              <input
                type="email"
                placeholder="email@example.com"
                value={ct.email}
                onChange={(e) => updateContact(i, "email", e.target.value)}
                className="flex-1 h-7 px-2 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary/40"
                data-testid={`contractor-email-${contractor.type}-${i}`}
              />
              <button
                type="button"
                onClick={() => removeContact(i)}
                className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                data-testid={`contractor-contact-remove-${contractor.type}-${i}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addContact}
            className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-primary transition-colors"
            data-testid={`contractor-add-contact-${contractor.type}`}
          >
            <Plus size={11} />
            Add contact
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function ContractorsCard({ project, onSave }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(() => mergeContractorsWithDefaults(project.contractors));
  const [saving, setSaving] = useState(false);
  const [newType, setNewType] = useState("");

  // Sync if project prop changes externally
  useEffect(() => {
    if (!editing) setLocal(mergeContractorsWithDefaults(project.contractors));
  }, [project.contractors, editing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave("contractors", local);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLocal(mergeContractorsWithDefaults(project.contractors));
    setEditing(false);
    setNewType("");
  };

  const updateContractor = (i, updated) =>
    setLocal((prev) => prev.map((c, idx) => (idx === i ? updated : c)));

  const removeContractor = (i) =>
    setLocal((prev) => prev.filter((_, idx) => idx !== i));

  const addCustomContractor = () => {
    const t = newType.trim();
    if (!t) return;
    if (local.some((c) => c.type.toLowerCase() === t.toLowerCase())) return;
    setLocal((prev) => [...prev, { type: t, is_default: false, not_needed: false, contacts: [] }]);
    setNewType("");
  };

  if (!editing) {
    return <ReadView contractors={local} onEdit={() => setEditing(true)} />;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="contractors-card-editing">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
          <HardHat size={12} className="text-slate-400" />
          Contractors
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCancel}
            className="text-[11px] text-slate-500 hover:text-slate-700 px-2.5 py-1 border border-slate-200 rounded transition-colors"
            data-testid="contractors-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 text-[11px] font-semibold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 px-2.5 py-1 rounded transition-colors"
            data-testid="contractors-save-btn"
          >
            <Check size={11} />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {local.map((c, i) => (
          <ContractorEditRow
            key={`${c.type}-${i}`}
            contractor={c}
            onChange={(updated) => updateContractor(i, updated)}
            onRemove={() => removeContractor(i)}
          />
        ))}
      </div>

      {/* Add custom contractor */}
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
        <input
          type="text"
          placeholder="Add contractor type (e.g. Civil)"
          value={newType}
          onChange={(e) => setNewType(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomContractor(); }}}
          className="flex-1 h-8 px-3 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary/40"
          data-testid="new-contractor-type-input"
        />
        <button
          type="button"
          onClick={addCustomContractor}
          disabled={!newType.trim()}
          className="flex items-center gap-1 h-8 px-3 text-xs font-medium text-primary border border-primary/30 rounded hover:bg-primary/5 disabled:opacity-40 transition-colors"
          data-testid="add-custom-contractor-btn"
        >
          <Plus size={12} />
          Add
        </button>
      </div>
    </div>
  );
}
